/**
 * useUsersDB — manages public.profiles in Supabase.
 * No mock data. No fake users. Real errors surfaced.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export type ProfileRole = "super_admin" | "admin" | "editor" | "author" | "columnist";

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: ProfileRole;
  is_active: boolean;
  avatar_url: string | null;
  created_at: string;
  last_sign_in_at: string | null;
}

export const profileRoleLabels: Record<ProfileRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  editor: "Editor",
  author: "Autor",
  columnist: "Kolumnist",
};

export const profileRoleColors: Record<ProfileRole, string> = {
  super_admin: "bg-red-100 text-red-700",
  admin: "bg-orange-100 text-orange-700",
  editor: "bg-amber-100 text-amber-700",
  author: "bg-emerald-100 text-emerald-700",
  columnist: "bg-teal-100 text-teal-700",
};

export const ALL_PROFILE_ROLES: ProfileRole[] = [
  "super_admin",
  "admin",
  "editor",
  "author",
  "columnist",
];

/** Role → list of admin panel permission keys */
export const rolePermissions: Record<ProfileRole, string[]> = {
  super_admin: [
    "dashboard", "articles", "categories", "authors", "media", "ads", "homepage", "breaking", "seo", "newsletter",
    "pages", "settings", "users", "analytics", "brand",
  ],
  admin: [
    "dashboard", "articles", "categories", "authors", "media", "ads", "homepage", "breaking", "seo", "newsletter",
    "pages", "settings", "users", "analytics", "brand",
  ],
  editor: [
    "dashboard", "articles", "categories", "authors", "media", "breaking", "newsletter", "pages", "seo",
  ],
  author: ["dashboard", "articles", "media"],
  columnist: ["dashboard", "articles", "media"],
};

export interface UsersDBError {
  message: string;
  detail?: string;
}

function isFunctionNotDeployedError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as { message?: string }).message ?? "";
  return (
    msg.includes("404") ||
    msg.toLowerCase().includes("not found") ||
    msg.includes("FunctionsHttpError") ||
    msg.includes("Failed to fetch")
  );
}

export function useUsersDB() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<UsersDBError | null>(null);

  // ── Fetch all profiles from public.profiles ──────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, name, role, is_active, avatar_url, created_at, last_sign_in_at")
        .order("created_at", { ascending: false });

      if (error) {
        setFetchError({
          message: "Fehler beim Laden der Benutzer aus public.profiles.",
          detail: error.message,
        });
        setUsers([]);
      } else {
        setUsers((data as UserProfile[]) ?? []);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unbekannter Fehler";
      setFetchError({ message: "Verbindungsfehler zu Supabase.", detail: msg });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Update role of an existing profile row ───────────────────────────────
  const updateUserRole = useCallback(
    async (userId: string, role: ProfileRole): Promise<UsersDBError | null> => {
      const { error } = await supabase
        .from("profiles")
        .update({ role })
        .eq("id", userId);

      if (error) {
        return { message: "Rolle konnte nicht gespeichert werden.", detail: error.message };
      }
      await fetchUsers();
      return null;
    },
    [fetchUsers]
  );

  // ── Toggle is_active ─────────────────────────────────────────────────────
  const toggleUserActive = useCallback(
    async (userId: string, is_active: boolean): Promise<UsersDBError | null> => {
      const { error } = await supabase
        .from("profiles")
        .update({ is_active })
        .eq("id", userId);

      if (error) {
        return { message: "Status konnte nicht geändert werden.", detail: error.message };
      }
      await fetchUsers();
      return null;
    },
    [fetchUsers]
  );

  /**
   * Assign a role to a user by email.
   *
   * PATH 1 — Edge Function "assign-user-role" (preferred, uses service role key).
   * PATH 2 — Direct upsert into public.profiles by email (fallback).
   *
   * The upsert in PATH 2 works even if no profile row exists yet:
   * it inserts a new row with only email + role (no auth.users FK required
   * because profiles.id is not forced here — we match on email).
   *
   * NOTE: If the profiles table has a FK on id → auth.users, PATH 2 can only
   * update existing rows. In that case deploy the Edge Function for full support.
   */
  const assignRoleByEmail = useCallback(
    async (
      email: string,
      role: ProfileRole,
      name: string
    ): Promise<UsersDBError | null> => {
      const normalizedEmail = email.trim().toLowerCase();

      // ── PATH 1: Edge Function ─────────────────────────────────────────────
      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          "assign-user-role",
          { body: { email: normalizedEmail, role, name: name.trim() } }
        );

        if (!fnError) {
          if (fnData?.error) {
            return { message: fnData.error as string, detail: fnData.detail as string | undefined };
          }
          if (fnData?.success) {
            await fetchUsers();
            return null;
          }
        }

        if (fnError && !isFunctionNotDeployedError(fnError)) {
          return {
            message: "Fehler beim Zuweisen der Rolle (Edge Function).",
            detail: fnError.message,
          };
        }
        // 404 / not deployed → fall through to PATH 2
      } catch {
        // Network error or not deployed → fall through to PATH 2
      }

      // ── PATH 2: Direct profile lookup + update ────────────────────────────
      const { data: existing, error: lookupError } = await supabase
        .from("profiles")
        .select("id, email")
        .eq("email", normalizedEmail)
        .maybeSingle();

      if (lookupError) {
        return { message: "Fehler beim Suchen des Benutzers.", detail: lookupError.message };
      }

      if (!existing) {
        return {
          message: `Kein Profil-Eintrag für „${normalizedEmail}" gefunden.`,
          detail:
            "Der Benutzer muss zuerst in Supabase Authentication erstellt werden und sich " +
            "mindestens einmal eingeloggt haben, damit ein Profil-Eintrag existiert. " +
            "Alternativ: Deployen Sie die Edge Function \"assign-user-role\" — dann " +
            "funktioniert die Zuweisung auch ohne vorherigen Login.",
        };
      }

      const updatePayload: Record<string, unknown> = { role };
      if (name.trim()) updatePayload.name = name.trim();

      const { error: updateError } = await supabase
        .from("profiles")
        .update(updatePayload)
        .eq("id", existing.id);

      if (updateError) {
        return { message: "Rolle konnte nicht zugewiesen werden.", detail: updateError.message };
      }

      await fetchUsers();
      return null;
    },
    [fetchUsers]
  );

  return {
    users,
    loading,
    fetchError,
    fetchUsers,
    updateUserRole,
    toggleUserActive,
    assignRoleByEmail,
  };
}
